import React, { useEffect, useState } from 'react';
import { _hospitalUrls, _pacsUrls } from '@custom/Utils/constants/apiUrls';
import { getCurrencySymbol } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getPrice from '@custom/rtk-apis/price/getPrice';
import deletePrice from '@custom/rtk-apis/price/deletePrice';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import { CustomTable, Pagination, Search, Loader, ConfirmationPopup, SuccessModal, getIcon } from '@ohif/ui';
import { servicesManager } from '../../app/src/App';
import AddEditPricing from './addEditPricing';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { useNavigate } from 'react-router-dom';

interface PricingProps {
  activeTab: string;
  setActiveTab?: (tab: string) => void;
}

const Pricing = (props: PricingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { result: pricingData, loading, error, pagination } = useSelector((state: RootState) => state.getPriceSliceResponse);
  const { result: deleteData, loading: deleteLoader, error: deleteError, msg } = useSelector((state: RootState) => state.deletePriceSliceResponse);
  const { status, loading: addLoading, result: addResult, msg: addMsg } = useSelector((state: RootState) => state?.addEditPriceSliceResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [load, setLoad] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [success, setSuccess] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalityModal, setModalityModal] = useState(false);
  const [currency, setCurrency] = useState("");
  const [editData, setEditData] = useState(null);

  const { uiNotificationService } = servicesManager.services;

  useEffect(() => {
    dispatch(getModalityStudy());
    dispatch(getBillingGeneralSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPrice({ search: searchQuery, page: pgNumber, limit: pgLimit }));
  }, [dispatch, searchQuery, pgNumber, pgLimit])

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  useEffect(() => {
    if (pricingData) {
      setLoad(true);
      setTableData(
        pricingData.map((item) => ({
          ...item,
          price: `${getCurrencySymbol(billingGeneralSettingsData?.result?.currency)}${item.price}`,
        }))
      );
      setLoad(false);
    }
  }, [pricingData, billingGeneralSettingsData, currency]);

  const filterTableData = (data, query) => {
    return data.filter(item =>
      item.modality.toLowerCase().includes(query.toLowerCase()) ||
      item.studyRegion.toLowerCase().includes(query.toLowerCase())
    );
  };

  const columns = [
    { key: 'modality', title: 'Modality', sort: true },
    { key: 'studyRegion', title: 'Study Region' },
    { key: 'price', title: 'Price' },
    { key: 'actions', title: 'Action' }
  ];

  const handleEditClick = (item) => {
    const fullItem = pricingData.find((i) => i.id === item.id);
    setEditData(fullItem);
    setShowAddForm(true);
  };

  const actions = [
    {
      id: 'editPricing',
      label: 'Edit',
      iconName: 'edit',
      onClick: handleEditClick,
    },
    {
      id: 'deletePricing',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      }
    },
  ];

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const toggleAddForm = () => {
    if (billingGeneralSettingsData?.msg === "General settings not found for this hospital.") {
      props.setActiveTab?.('general');
      uiNotificationService.show({
        title: 'Billing Settings',
        message: 'Fill the general settings to create appointment.',
        type: 'error',
      });
      navigate('/billing');
    } else {
      setEditData(null);
      setShowAddForm(!showAddForm);
    }
  };

  const searchClick = (searchText) => {
    setSearchQuery(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
    dispatch(getPrice({ search: searchText, page: 1, limit: pgLimit }));
  };

  const handleDelete = async () => {
    if (selectedRow) {
      const payload = {
        modality: selectedRow.modality,
        studyRegion: selectedRow.studyRegion,
        id: selectedRow.id
      };
      try {
        setIsPopupOpen(false);
        await dispatch(deletePrice(payload));
        await dispatch(getPrice({ search: searchQuery, page: pgNumber, limit: pgLimit }));
        setSuccess(true);
      } catch (error) {
        setIsPopupOpen(false);
      }
    }
  };

  return (
    <>
      <div className='page__body'>
        {showAddForm ? (
          <AddEditPricing setShowAddForm={setShowAddForm} priceModalityData={editData} />
        ) : (
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search
                    text="Search by Modality, Study Region & Price"
                    onSearchClick={searchClick}
                    value={searchInputValue}
                    setValue={setSearchInputValue}
                  />
                </div>
              </div>
              <div className='btn__group'>
                <button className="btn btn__white" onClick={toggleAddForm}>
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Add Price
                </button>
              </div>
            </div>
            <div className='tableContainer'>
              <CustomTable
                columns={columns}
                data={filterTableData(tableData, searchQuery)}
                actions={actions}
                from='pricing'
              />
            </div>
          </div>
        )}
      </div>
      <Loader isLoaderOpen={load || deleteLoader || addLoading} />
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete ${selectedRow?.modality}?`}
        onConfirm={handleDelete}
        onCancel={handleCancel}
        title={''}
      />
      <SuccessModal
        isSuccessModalOpen={success}
        title={msg}
        handleCloseModal={() => setSuccess(false)}
      />
      <SuccessModal
        isSuccessModalOpen={modal}
        title={addMsg || "Price added successfully"}
        handleCloseModal={() => setModal(false)}
      />
      <SuccessModal
        isSuccessModalOpen={modalityModal}
        title={''}
        handleCloseModal={() => setModalityModal(false)}
      />
      {tableData.length > 0 && !showAddForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={pagination?.totalItems} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default Pricing;
